/**
 * FloatingActionBar - Glass Morphism Action Card
 * 
 * Mockup-exact:
 * - Overlaps hero with negative margin-top (-32px)
 * - Glass: rgba(255,255,255,0.78), backdrop-blur 16px
 * - Radius 24px, soft shadow
 * - 3 actions: Anrufen / Route / Bewerten
 * - Icon above, label below
 * - Tap: scale(0.96) + transition
 */

import React from 'react';
import { Phone, Navigation, Star } from 'lucide-react';

interface FloatingActionBarProps {
  hasPhone: boolean;
  onCall: () => void;
  onRoute: () => void;
  onReview: () => void;
  className?: string;
}

export const FloatingActionBar: React.FC<FloatingActionBarProps> = ({
  hasPhone,
  onCall,
  onRoute,
  onReview,
  className = '',
}) => {
  const actions = [
    {
      id: 'call',
      icon: Phone,
      label: 'Anrufen',
      onClick: onCall,
      disabled: !hasPhone,
      color: '#0E7F4F',
    },
    {
      id: 'route',
      icon: Navigation,
      label: 'Route',
      onClick: onRoute,
      disabled: false,
      color: '#2563EB',
    },
    {
      id: 'review',
      icon: Star,
      label: 'Bewerten',
      onClick: onReview,
      disabled: false,
      color: '#F59E0B',
    },
  ];

  return (
    <div 
      className={`relative z-30 mx-4 ${className}`}
      style={{ marginTop: '-32px' }}
    >
      <div 
        className="px-5 py-4"
        style={{
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: '20px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.04)',
          border: '1px solid rgba(255,255,255,0.6)',
        }}
      >
        <div className="flex justify-around items-center">
          {actions.map((action) => (
            <button
              key={action.id}
              onClick={action.onClick}
              disabled={action.disabled}
              className={`flex flex-col items-center gap-2 min-w-[76px] py-1 transition-colors duration-150 ${
                action.disabled 
                  ? 'opacity-35 cursor-not-allowed' 
                  : 'active:scale-95 hover:opacity-90'
              }`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <div 
                className="w-12 h-12 rounded-2xl flex items-center justify-center transition-transform"
                style={{
                  background: action.disabled 
                    ? 'rgba(156,163,175,0.15)' 
                    : `${action.color}14`,
                }}
              >
                <action.icon 
                  className="h-5 w-5" 
                  strokeWidth={1.8}
                  style={{ color: action.disabled ? '#9CA3AF' : action.color }}
                />
              </div>
              <span 
                className="text-xs font-semibold"
                style={{ color: action.disabled ? '#9CA3AF' : '#374151' }}
              >
                {action.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FloatingActionBar;
